// Story 8.15 (ADO-2024), D-9 and D-10 (C-1): what happened to an auto-sent cart, as design-system
// Banners, each with Dismiss (for everyone on the cart, so it does not come back on the next visit
// or another device). Figma has no frame for them (story Design section): the copy is the ticket
// comment's and the story's.
//
//   <AutoSendOutcomeBanners />                       // Select Depot: every outcome
//   <AutoSendOutcomeBanners cart={cartTarget} />     // the cart page: that cart's failures
//
// - `SENT_WITH_HHT_WAITING`: info, "Order auto-sent without HHT orders": the cart went just
//   before the cut-off while HHT orders waited, and they still wait (8.10 DV-57).
// - `REFUSED` and `MISSED`: error, "Your <depot> order was not sent automatically", with the
//   server's plain-words reason. The cart is unchanged, so the Shopper can still send it by hand.
// - The cart page names its cart (the depot, the account and the entry kind; `null` with none,
//   which shows nothing) and shows only that cart's failures: its own sent notice belongs to
//   Select Depot, where the HHT orders are picked up. A cart with no account chosen matches its
//   depot's; an outcome that names no entry kind matches any kind at its depot and account.
// The list is read on mount (`useAutoSendOutcomesStore`, shared when both mount) and shows nothing
// while it loads or when the read fails.

import { useEffect } from 'react';
import type { AutoSendOutcome } from '../api/auto-send-outcomes.client';
import type { CartTarget } from '../api/cart-target';
import { Banner } from '../design-system';
import { useAutoSendOutcomesStore } from './useAutoSendOutcomesStore';

/** The reason shown when the server gave none. */
const NO_REASON = 'Something went wrong.';

export interface AutoSendOutcomeBannersProps {
  /** Show only this cart's failures; every outcome when absent; nothing when `null`. */
  readonly cart?: CartTarget | null;
  readonly className?: string | undefined;
}

function isForCart(outcome: AutoSendOutcome, cart: CartTarget): boolean {
  return (
    outcome.kind !== 'SENT_WITH_HHT_WAITING' &&
    outcome.depotId === cart.depotId &&
    (cart.customerAccountCode === null ||
      outcome.customerAccountCode === cart.customerAccountCode) &&
    (outcome.entryKind === undefined || outcome.entryKind === cart.entryKind)
  );
}

export function AutoSendOutcomeBanners({ cart, className }: AutoSendOutcomeBannersProps) {
  const reads = cart !== null;
  const outcomes = useAutoSendOutcomesStore((s) => s.outcomes);
  useEffect(() => {
    if (reads) void useAutoSendOutcomesStore.getState().load();
  }, [reads]);

  if (cart === null || outcomes === null) return null;
  const shown =
    cart === undefined ? outcomes : outcomes.filter((outcome) => isForCart(outcome, cart));
  if (shown.length === 0) return null;
  const dismiss = (id: string) => void useAutoSendOutcomesStore.getState().dismiss(id);

  return (
    <div className={className} data-testid="auto-send-outcomes">
      {shown.map((outcome) =>
        outcome.kind === 'SENT_WITH_HHT_WAITING' ? (
          <Banner
            key={outcome.id}
            type="info"
            title="Order auto-sent without HHT orders"
            onDismiss={() => dismiss(outcome.id)}
          >
            {`Your ${outcome.depotName} order${
              outcome.orderNumber === null ? '' : ` ${outcome.orderNumber}`
            } was sent before the cut-off. Your HHT orders are still waiting.`}
          </Banner>
        ) : (
          <Banner
            key={outcome.id}
            type="error"
            title={`Your ${outcome.depotName} order was not sent automatically`}
            onDismiss={() => dismiss(outcome.id)}
          >
            {outcome.reason ?? NO_REASON}
          </Banner>
        ),
      )}
    </div>
  );
}
